import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useAppForm } from '@sparklayer-io/spark-dash-core/lib/app-form';
import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardPageProps } from '../../../lib/dashboard-page';
import { AuthAlert } from '../components/auth-alert';
import { AuthButton } from '../components/auth-button';
import { AuthLayout } from '../components/auth-layout';
import { AuthLogo } from '../components/auth-logo';
import { TextInput } from '../components/text-input';
import { internalPath } from '../lib/internal-path';

const PAGE = 'features:auth.pages.login_page' satisfies I18nPrefix;

type LoginPageProps = {
  isPartner: boolean;
};

export function LoginPage({
  handlers,
  pageProps = { isPartner: false },
}: DashboardPageProps<LoginPageProps>) {
  const { apiHandler, authHandler, navigationHandler } = handlers;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const upsellItems = [
    {
      key: 'forms',
      title: t(`${PAGE}.upsell.forms.title`),
      description: t(`${PAGE}.upsell.forms.description`),
      link: 'https://docs.sparklayer.io/forms',
    },
    {
      key: 'catalogs',
      title: t(`${PAGE}.upsell.catalogs.title`),
      description: t(`${PAGE}.upsell.catalogs.description`),
      link: 'https://docs.sparklayer.io/b2b-catalogs',
    },
    {
      key: 'quoting',
      title: t(`${PAGE}.upsell.quoting.title`),
      description: t(`${PAGE}.upsell.quoting.description`),
      link: 'https://docs.sparklayer.io/quoting',
    },
    {
      key: 'discounts',
      title: t(`${PAGE}.upsell.discounts.title`),
      description: t(`${PAGE}.upsell.discounts.description`),
      link: 'https://docs.sparklayer.io/discounts',
    },
  ];

  const form = useAppForm({
    defaultValues: { email: '', password: '' },
    onSubmit: async ({ value }) => {
      setError(null);
      setLoading(true);
      try {
        await apiHandler.fetch({ method: 'GET', path: '/sanctum/csrf-cookie', maxRedirects: 0 });

        const res = await apiHandler.fetch<{ two_factor?: boolean }>({
          method: 'POST',
          path: '/login',
          payload: { email: value.email, password: value.password },
        });

        if (res.two_factor) {
          navigationHandler.navigate('/two-factor-challenge', {
            query: navigationHandler.query(),
            excludeSiteId: true,
            excludeSiteEnv: true,
          });
          return;
        }

        await authHandler.reloadUser();
        const from = internalPath(navigationHandler.query().from);
        navigationHandler.navigate(from ?? (pageProps.isPartner ? '/partners' : '/'), {
          excludeSiteId: true,
          excludeSiteEnv: true,
        });
      } catch (err) {
        const status = err instanceof ApiResponseError ? err.status : undefined;
        if (status === 422) {
          setError(t(`${PAGE}.validation_error`));
        } else if (status === 429) {
          setError(t(`${PAGE}.rate_limited_error`));
        } else {
          console.error(err);
          setError(t('errors:generic'));
        }
      }
      setLoading(false);
    },
  });

  return (
    <AuthLayout>
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <AuthLogo />
        <div className="mt-8 rounded-lg shadow-xl">
          <div className="rounded-tl-lg rounded-tr-lg bg-white p-10 sm:p-14">
            <div>
              <h2 className="mb-4 font-bold text-gray-900 text-xl sm:text-2xl">
                {t(`${PAGE}.heading`)}
              </h2>
              <p className="max-w mt-2 text-gray-600 text-sm">
                {t(`${PAGE}.register_prompt`)}{' '}
                <button
                  type="button"
                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500"
                  onClick={() =>
                    navigationHandler.navigate('/register', {
                      excludeSiteId: true,
                      excludeSiteEnv: true,
                    })
                  }
                >
                  {t(`${PAGE}.register_link`)}
                </button>
              </p>
            </div>

            <div className="mt-6">
              <form
                className="space-y-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  void form.handleSubmit();
                }}
              >
                {error && <AuthAlert type="error">{error}</AuthAlert>}

                <form.Field name="email">
                  {(field) => (
                    <TextInput
                      id="email"
                      name="email"
                      type="email"
                      label={t(`${PAGE}.email_label`)}
                      autoComplete="email"
                      required
                      placeholder={t(`${PAGE}.email_placeholder`)}
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  )}
                </form.Field>

                <form.Field name="password">
                  {(field) => (
                    <TextInput
                      id="password"
                      name="password"
                      type="password"
                      label={t(`${PAGE}.password_label`)}
                      autoComplete="current-password"
                      required
                      placeholder={t(`${PAGE}.password_placeholder`)}
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  )}
                </form.Field>

                <div>
                  <AuthButton loading={loading}>{t(`${PAGE}.submit`)}</AuthButton>
                </div>
                <div className="flex">
                  <div className="flex" />
                  <div className="text-sm">
                    <button
                      type="button"
                      className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500"
                      onClick={() =>
                        navigationHandler.navigate('/forgotten-password', {
                          excludeSiteId: true,
                          excludeSiteEnv: true,
                        })
                      }
                    >
                      {t(`${PAGE}.forgot_password`)}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="relative hidden w-full overflow-hidden bg-[#0B2347] pb-4 sm:block">
            <div className="absolute inset-x-0 -top-12 left-85 m-auto h-80 max-w-lg bg-linear-to-tr from-indigo-400 to-[#C084FC] opacity-90 blur-[118px]" />

            {upsellItems.map((item, index) => (
              <input
                key={item.key}
                id={`carousel-${index}`}
                type="radio"
                name="carousel"
                className="sr-only"
                defaultChecked={index === 0}
              />
            ))}
            {upsellItems.map((item, index) => (
              <label key={item.key} htmlFor={`carousel-${index}`} className="hidden">
                {t(`${PAGE}.upsell.slide`, { number: index + 1 })}
              </label>
            ))}

            <div className="flex transition-transform duration-700 ease-in-out">
              {upsellItems.map((item) => (
                <div key={item.key} className="relative min-w-full px-12 py-5">
                  <div className="relative z-10 flex items-start gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex rounded bg-linear-to-r from-indigo-500 via-purple-500 to-pink-500 px-1.25 py-0.5 text-white text-xs leading-none">
                          {t(`${PAGE}.upsell.badge`)}
                        </span>
                        <p className="font-medium text-gray-100 text-sm leading-5">{item.title}</p>
                      </div>
                      <p className="mt-0 inline-flex gap-1 text-gray-300 text-sm">
                        {item.description}
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center font-medium text-gray-200 hover:text-indigo-200"
                        >
                          <span>{t(`${PAGE}.upsell.more`)}</span>
                          <ArrowTopRightOnSquareIcon className="ml-1 size-4 group-hover:text-text-indigo-200" />
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="absolute right-0 bottom-0 left-0 mb-4 inline-flex justify-center space-x-2">
              {upsellItems.map((item, index) => (
                <label
                  key={item.key}
                  htmlFor={`carousel-${index}`}
                  aria-label={t(`${PAGE}.upsell.slide`, { number: index + 1 })}
                  className="carousel-dot size-2.5 cursor-pointer rounded-full bg-white transition-all hover:bg-pink-300"
                />
              ))}
            </div>
          </div>

          <div className="h-2 w-full rounded-lg rounded-tl-none rounded-tr-none bg-linear-to-r from-indigo-500 via-purple-500 to-pink-500 lg:block" />
        </div>
        <p className="max-w pt-10 text-gray-600 text-sm">
          <a
            href="https://www.sparklayer.io"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            <span aria-hidden="true">← </span>
            {t(`${PAGE}.return_to_homepage`)}
          </a>
        </p>
      </div>
    </AuthLayout>
  );
}
